<template>
  <div class="view-product-register-module-lesson-basic">
    <div class="my-6">
      <step-title
        title="Informações básicas"
        description="Configure a aula"
        icon="fa-solid fa-info"
        fa
      />
    </div>
    <form-group
      v-model="form.name"
      :errors="errors.name"
      id="name"
      placeholder="Ex: Apresentação do curso"
      label="Nome da aula"
      :maxLength="240"
      required
    />
    <div class="d-flex justify-content-end">
      <small
        class="text-secondary text-right mb-5 w-100"
        :class="{
          'text-danger': form.name && form.name.length >= 240
        }"
      >
        {{ `${form.name ? form.name.length : 0}/240` }}
      </small>
    </div>
    <action-dispatcher
      action="membersModules/get"
      :parameters="{
        product_id: $route.params.id,
        limit: 1000
      }"
    >
      <form-group
        v-model="form.module_id"
        :errors="errors.module_id"
        id="module_id"
        placeholder="Selecione"
        label="Módulo"
        select
        :options="modulesOptions"
      />
    </action-dispatcher>
    <form-group
      v-model="form.link_media"
      :errors="errors.link_media"
      id="link_media"
      placeholder="Insira a URL do vídeo, áudio ou o link do Zoom"
      label="Link da mídia"
      class="pb-0"
    />
    <small class="small-text text-secondary">
      Seus arquivos de video ou áudio precisam estar hospedados em um serviço,
      como YouTube, Vimeo, SoundCloud, etc. Atenção: no app mobile não haverá
      suporte de download de vídeos do YouTube e Vimeo, apenas vídeos HLS.
    </small>

    <template v-if="isZoomLink">
      <form-group
        v-model="form.meeting_password"
        :errors="errors.meeting_password"
        id="meeting_password"
        placeholder="Digite a senha da sua chamada do Zoom"
        label="Senha da chamada do Zoom"
        class="pb-0"
      />
      <small class="small-text text-secondary">
        Insira a senha da sua chamada no zoom para que seus clientes não
        precisem digita-la ao entrar em sua aula ao vivo.
      </small>
      <form-group
        v-model="form.meeting_view_type"
        :errors="errors.meeting_view_type"
        select
        :options="meetingViewTypeOptions"
        id="meeting_view_type"
        placeholder="Escolha o tipo de aula ao vivo"
        label="Tipo da aula ao vivo"
        class="pb-0"
      />
      <small class="small-text text-secondary">
        Escolha o tipo da sua aula ao vivo, controlando se seus alunos verão
        somente você na chamada do Zoom, ou se todos aparecerão em galeria.
      </small>
    </template>

    <div class="form-group pt-6 mb-6">
      <label>Texto de apoio (descrição)</label>
      <ckeditor
        :editor="editor"
        v-model="form.description"
        :config="ckeditorConfig"
      />
    </div>
    <tag-input
      label="Tags"
      v-model="form.tags"
      placeholder="Separe as tags com vírgulas"
      class="m-0"
    />
    <small class="small-text text-secondary">
      Os alunos poderão filtrar as aulas com tags que você definir.
    </small>

    <hr class="my-6" />

    <h6 class="subtitle m-0">
      Tempo da aula
    </h6>
    <small class="description">
      Informe quanto tempo de duração tem a aula
    </small>

    <form-group
      v-model="form.duration"
      :errors="errors.duration"
      mask="###########"
      id="duration"
      placeholder="00min"
      label="Minutos"
      class="pb-0"
    />
    <small class="small-text text-secondary">
      Informe o tempo da aula em minutos. Exemplo: uma aula com 1h35 de duração,
      você deve inserir 95 minutos.
    </small>

    <hr class="my-6" />

    <h6 class="subtitle m-0">
      Conteúdo complementar
    </h6>
    <small class="description">
      Insira aqui materiais complementares para estudo.
    </small>

    <div class="pt-6">
      <dashed-add-resource
        icon="upload"
        title="Upload de arquivos"
        subTitle="Adicione até 10 arquivos que tenham no máximo 100MB cada. Os formatos permitidos são jpg, gif, png, pdf, ppt, doc, docx, xls, xlsx, zip, rar, epub, bmp, mp3. Importante: o título do arquivo deve ter até 100 caracteres"
        @onClick="openFilePicker('lesson-file-input')"
        class="mb-5"
        :stars="false"
      >
        <input
          type="file"
          id="lesson-file-input"
          @change="onFileChange($event.target.files)"
          multiple
        />
      </dashed-add-resource>
      <div class="text-center" v-if="loadingFile">
        <b-spinner variant="primary" />
      </div>
      <div
        v-else
        class="file-wrapper d-flex align-items-center px-5 mb-5"
        v-for="(file, index) in extra_files"
        :key="index"
      >
        <div class="flex-fill">
          <i class="icon-pdf" />
          {{ file.name }}
        </div>
        <button
          class="btn btn-sm file-presentation"
          type="button"
          @click="onRemoveFile(index)"
        >
          <i class="icon-trash" />
        </button>
      </div>
    </div>

    <div class="pt-5">
      <dashed-add-resource
        icon="link"
        title="Links externos"
        subTitle="Adicione links externos com conteúdos complementares."
        class="mb-5"
        @onClick="visible.extra_reading = true"
        :stars="false"
      />
      <div
        v-for="(reading, index) in form.extra_reading"
        :key="index"
        class="complementary-reading mb-5 pl-6 py-5"
      >
        <div class="d-flex align-items-center">
          <h6 class="flex-fill">{{ reading.title }}</h6>
          <button
            class="btn btn-square-sm btn-dark btn-sm"
            type="button"
            @click="onEditReading(reading, index)"
          >
            <i class="fa-solid fa-pen" />
          </button>
          <button
            class="btn btn-square-sm btn-dark btn-sm mx-3"
            type="button"
            @click="onRemoveReading(index)"
          >
            <i class="icon-trash" />
          </button>
        </div>
        <div class="d-flex align-items-center">
          <span class="text-muted">
            {{ reading.site_name }}
          </span>
          <div class="dot mx-3"></div>
          <span class="text-primary">
            {{ reading.link }}
          </span>
        </div>
      </div>
    </div>

    <hr class="my-6" />

    <h6 class="subtitle m-0">
      Liberação do conteúdo
    </h6>
    <small class="description">
      Determine em quantos dias o seu conteúdo será liberado após a compra.
    </small>

    <b-form-checkbox
      id="_days_to_release"
      v-model="days_to_release"
      class="mt-6 button"
      switch
    >
      Liberar após quantidade de dias
    </b-form-checkbox>

    <div v-if="days_to_release">
      <form-group
        v-model="form.days_to_release"
        :errors="errors.days_to_release"
        id="days_to_release"
        placeholder="0 dias"
        label="Liberar após"
        mask="###"
      />
    </div>

    <b-form-checkbox
      v-model="release_at"
      id="_release_at"
      switch
      class="mt-5 button"
    >
      Liberar em uma data específica
    </b-form-checkbox>

    <div v-if="release_at">
      <form-group
        v-model="form.release_at"
        :errors="errors.release_at"
        id="release_at"
        placeholder="00/00/0000"
        label="Liberar no dia"
        mask="##/##/####"
        masked
      />
    </div>

    <hr class="my-6" />

    <h6 class="subtitle mb-6">
      Comentários
    </h6>

    <b-form-checkbox
      id="comments_enabled"
      v-model="form.comments_enabled"
      class="mb-9"
      switch
    >
      Liberar comentários
    </b-form-checkbox>

    <responsive-modal v-model="visible.extra_reading" size="lg">
      <div class="confirmation p-5">
        <div class="d-flex align-items-center justify-content-between pb-3">
          <span class="fs-18">
            Conteúdo complementar
          </span>
          <button
            class="btn btn-sm btn-square"
            @click="visible.extra_reading = false"
          >
            <i class="icon-x fs-20 text-secondary" />
          </button>
        </div>

        <div class="row">
          <div class="col-12">
            <form-group
              v-model="extra_reading.link"
              id="link"
              label="Link do conteúdo"
              placeholder="Digite a URL do conteúdo"
              :errors="[]"
            />
            <form-group
              v-model="extra_reading.site_name"
              id="site_name"
              label="Nome do site"
              placeholder="Ex: Revista Forbes"
              :errors="[]"
            />
            <form-group
              v-model="extra_reading.title"
              id="title"
              label="Título do artigo"
              placeholder="Ex: Negócios do futuro"
              :errors="[]"
            />
          </div>

          <div class="col-12 mt-5 d-lg-none">
            <button
              class="btn btn-primary btn-40 btn-block"
              type="button"
              @click="onSaveReadings"
            >
              {{
                extra_reading.editedIndex !== undefined ? "Salvar" : "Adicionar"
              }}
            </button>
          </div>
          <div class="col-12 mt-5 d-lg-none">
            <button
              class="btn btn-secondary btn-40 btn-block"
              type="button"
              @click="visible.extra_reading = false"
            >
              Cancelar
            </button>
          </div>
        </div>
        <div
          class="d-none d-lg-flex justify-content-end align-items-center mt-5"
        >
          <button
            class="btn btn-secondary btn-40"
            type="button"
            @click="visible.extra_reading = false"
          >
            Cancelar
          </button>

          <button
            class="btn btn-primary btn-40 ml-5"
            type="button"
            @click="onSaveReadings"
          >
            {{
              extra_reading.editedIndex !== undefined ? "Salvar" : "Adicionar"
            }}
          </button>
        </div>
      </div>
    </responsive-modal>
  </div>
</template>

<script>
import DashedAddResource from "@/components/product/details/content/step02/DashedAddResource.vue";
import { is_file_size_exceeds_limit_in_mb } from "@/functions/helpers";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import get from "lodash/get";
import { mapState } from "vuex";

export default {
  components: {
    DashedAddResource
  },
  props: {
    form: {
      type: Object,
      default: () => ({})
    },
    errors: {
      type: Object,
      default: () => ({})
    },
    lesson: {
      type: Object,
      default: () => ({})
    },
    files: {
      type: Array,
      default: () => []
    }
  },
  data() {
    const f = field => (get(this.lesson, field) || "").substr(0, 19);
    return {
      days_to_release: !!get(this.lesson, "days_to_release", 0),
      release_at: f("release_at") && f("release_at") !== f("published_at"),
      loadingFile: false,
      extra_files: this.form.extra_files || [],
      extra_reading: {
        title: "",
        site_name: "",
        link: "",
        editedIndex: undefined
      },
      visible: {
        extra_reading: false,
        file_upload: false
      },
      editor: ClassicEditor,
      ckeditorConfig: {
        toolbar: {
          items: [
            "undo",
            "redo",
            "|",
            "heading",
            "|",
            "bold",
            "italic",
            "|",
            "link",
            "blockQuote",
            "|",
            "bulletedList",
            "numberedList",
            "outdent",
            "indent"
          ],
          shouldNotGroupWhenFull: false
        },
        language: "pt-br"
      }
    };
  },
  computed: {
    ...mapState("membersModules", ["modules"]),
    meetingViewTypeOptions() {
      return [
        {
          id: "speaker",
          name: "Aula ao vivo sem interação (Somente você aparece)"
        },
        {
          id: "gallery",
          name:
            "Aula ao vivo com interação (Você aparece juntamente com os alunos em galeria)"
        }
      ];
    },
    isZoomLink() {
      return !!`${this.form.link_media}`.match(".zoom.");
    },
    modulesOptions() {
      return this.modules.reduce(
        (prev, curr) => [
          ...prev,
          curr,
          ...curr.sub_modules.map(m => ({
            ...m,
            name: `${curr.name} > ${m.name}`
          }))
        ],
        []
      );
    }
  },
  methods: {
    openFilePicker(id) {
      document.getElementById(id).click();
    },
    onSaveReadings() {
      if (this.extra_reading.editedIndex !== undefined) {
        this.form.extra_reading.splice(
          this.extra_reading.editedIndex,
          1,
          this.extra_reading
        );
      } else {
        this.form.extra_reading = [
          ...this.form.extra_reading,
          this.extra_reading
        ];
      }
      this.extra_reading = {
        title: "",
        site_name: "",
        link: "",
        editedIndex: undefined
      };
      this.visible.extra_reading = false;
    },
    onEditReading(reading, editedIndex) {
      this.extra_reading = {
        ...reading,
        editedIndex
      };
      this.visible.extra_reading = true;
    },
    onRemoveReading(index) {
      this.form.extra_reading.splice(index, 1);
    },
    onFileChange(files) {
      if (files.length) {
        if (is_file_size_exceeds_limit_in_mb(files[0].size, 100)) {
          this.$message.error("O arquivo deve ter no máximo 100MB");
          return;
        }
        this.extra_files = [...this.extra_files, ...files];
      }
    },
    onRemoveFile(index) {
      this.extra_files = this.form.extra_files.filter((_, i) => i !== index);
    }
  },
  watch: {
    extra_files(files) {
      this.form.extra_files = files;
    },
    "form.extra_files"(files) {
      this.extra_files = files;
    },
    days_to_release(v) {
      if (!v) this.form.days_to_release = 0;
    },
    release_at(v) {
      if (!v) this.form.release_at = null;
    }
  }
};
</script>
<style lang="scss" scoped>
@import "@/assets/scss/_variables.scss";

.view-product-register-module-lesson-basic {
  h5 {
    display: flex;
    align-items: center;
    i {
      font-size: 28px;
    }
  }

  .complementary-reading {
    border: 1px solid #d2d0d8;
    border-radius: 4px;
    padding: 10px;
  }

  .dot {
    width: 5px;
    height: 5px;
    background-color: #222;
    border-radius: 3px;
  }

  .file-wrapper {
    border: 1px solid #d2d0d8;
    border-radius: 4px;
    font-size: 12px;
  }

  .file-presentation {
    i {
      font-size: 26px;
      color: red;
    }
  }

  .subtitle {
    color: #3b3b4ae6;
    font-size: 16px;
    font-weight: 400;
  }

  .description {
    color: #77788ede;
    font-size: 14px;
  }

  .button {
    color: #3b3b4ae6;
    font-size: 15px;
    font-weight: 400;
  }

  .subdescription {
    color: #595463;
    font-size: 14px;
    font-weight: 400;
  }

  ::v-deep.ck-content {
    height: 200px !important;
  }
}
</style>
